import React, { useState } from 'react';
import Select, { StylesConfig, components, DropdownIndicatorProps } from 'react-select';
import * as colors from 'src/styles/colors';
import { BsFillCaretDownFill, BsFillCaretUpFill } from 'react-icons/bs';
import { PrimaryColors } from 'src/types/globalTypes';
import styled from 'styled-components/macro';

const Wrapper = styled.div<{ isCustomized: boolean; width?: string }>`
  position: relative;
  width: ${(props) => props.width};
  span {
    position: absolute;
    pointer-events: none;
    left: 15px;
    top: -2px;
    font-size: 12px;
    color: ${colors.BlueGray_500};
  }
`;

type IValue = string | number;

export interface IOption {
  value: IValue;
  label: string;
}

export const placeholderOption: IOption = { value: '', label: '請選擇' };
interface IProps {
  onChange: (value: IValue) => void;
  options: Array<IOption>;
  defaultValue?: IValue;
  value?: IValue;
  isDisabled?: boolean;
  primaryColors?: PrimaryColors;
  customizedTitle?: string;
  width?: string;
  isBigForm?: boolean;
  isPill?: boolean;
  inputId?: string;
  ariaLabelValue? : string;
}

/**
 * 通用下拉選單。
 */
const ReactSelect: React.FC<IProps> = ({
  onChange,
  options,
  defaultValue,
  value,
  isDisabled,
  primaryColors,
  customizedTitle,
  width,
  isBigForm,
  isPill,
  inputId,
  ariaLabelValue,
}) => {
  const optionMap = new Map(options.map((item) => [item.value, item]));
  // defaultValue 有給值且 option 中找到則顯示，反之顯示第一個選項。
  const defaultSelected = (defaultValue && optionMap.get(defaultValue)) || options[0];
  const [selected, setSelected] = useState<IOption | undefined | null>(defaultSelected);

  const handleChange = (option: IOption | null) => {
    setSelected(option);
    option && onChange(option.value);
  };

  /** 替換選單箭頭icon */
  const DropdownIndicator = (props: DropdownIndicatorProps<IOption>) => {
    const { selectProps } = props;
    return (
      <components.DropdownIndicator {...props}>
        {selectProps.menuIsOpen ? <BsFillCaretUpFill /> : <BsFillCaretDownFill />}
      </components.DropdownIndicator>
    );
  };

  type IsMulti = false;
  const customStyles: StylesConfig<IOption, IsMulti> = {
    container: (styles) => ({
      ...styles,
      width: '100%',
      textAlign: 'initial',
    }),
    control: (styles) => ({
      ...styles,
      cursor: 'pointer',
      height: isBigForm ? '57px' : '40px',
      borderColor: primaryColors ? primaryColors.third : colors.primaryBlue_100,
      boxShadow: 'none',
      overflow: 'hidden',
      '&:hover': {
        borderColor: primaryColors ? primaryColors.seventh : colors.primaryBlue_500,
        boxShadow: 'none',
      },
    }),
    singleValue: (styles) => ({
      ...styles,
      color: colors.BlueGray_800,
      padding: customizedTitle ? '18px 2px 0px 4px' : '0px 2px',
    }),
    menu: (styles) => ({
      ...styles,
      width: 'max-content',
      minWidth: '100%',
    }),
    menuList: (styles) => ({
      ...styles,
      '::-webkit-scrollbar': {
        width: '8px',
        height: '8px',
      },
      '::-webkit-scrollbar-thumb': {
        borderRadius: '20px',
        boxShadow: 'inset 0 0 8px 8px' + colors.BlueGray_500,
        border: 'solid 2px transparent',
      },
    }),
    option: (styles, { isFocused, isSelected }) => ({
      ...styles,
      cursor: 'pointer',
      height: '40px',
      lineHeight: '40px',
      padding: '0px 12px',
      textAlign: 'left',
      fontFamily: isDisabled ? undefined : isSelected ? 'NotoSansTC-Bold' : undefined,
      backgroundColor: isDisabled
        ? undefined
        : isSelected
        ? primaryColors
          ? primaryColors.second
          : colors.primaryBlue_50
        : isFocused
        ? primaryColors
          ? primaryColors.second
          : colors.primaryBlue_50
        : undefined,
      color: isDisabled
        ? '#ccc'
        : isSelected
        ? primaryColors
          ? primaryColors.seventh
          : colors.primaryBlue_500
        : isFocused
        ? primaryColors
          ? primaryColors.seventh
          : colors.primaryBlue_500
        : undefined,
    }),
    valueContainer: (styles) => ({
      ...styles,
      padding: '0px 8px',
    }),
    input: (styles) => ({
      ...styles,
      padding: '0px 8px',
      margin: '0px',
    }),
    dropdownIndicator: (styles, { isFocused }) => ({
      ...styles,
      color: colors.BlueGray_800,
      fontSize: '10px',
      height: '100%',
      padding: isBigForm ? '20px' : '14px 15px',
      display: 'flex',
      justifyContent: 'center',
      justifyItems: 'center',
      backgroundColor: isDisabled
        ? undefined
        : isFocused
        ? primaryColors
          ? primaryColors.second
          : colors.primaryBlue_50
        : undefined,
    }),
  };

  const pillStyles: StylesConfig<IOption, IsMulti> = {
    control: (styles, { isFocused }) => ({
      ...styles,
      cursor: 'pointer',
      height: '26px',
      borderRadius: '20px',
      border: isFocused ? '2px solid' :'none',
      boxShadow: 'none',
      minHeight: 'unset',
      '&:hover': {
        backgroundColor: primaryColors ? primaryColors.second : colors.primaryBlue_50,
        border: 'none',
        boxShadow: 'none',
      },
      '&:focus': {
        border: 'none',
        boxShadow: 'none',
      },
    }),
    singleValue: (styles) => ({
      ...styles,
      color: colors.BlueGray_600,
    }),
    menu: (styles) => ({
      ...styles,
      width: 'max-content',
      minWidth: '100%',
      boxShadow: '2px 2px 8px 1px' + colors.BlueGray_100,
    }),

    option: (styles, { isFocused, isSelected }) => ({
      ...styles,
      cursor: 'pointer',
      padding: '8px 32px',
      textAlign: 'left',
      backgroundColor: isDisabled
        ? undefined
        : isSelected
        ? primaryColors
          ? primaryColors.second
          : colors.primaryBlue_50
        : isFocused
        ? primaryColors
          ? primaryColors.second
          : colors.primaryBlue_50
        : undefined,
      color: colors.BlueGray_500,
    }),
    valueContainer: (styles) => ({
      ...styles,
      padding: '0px 8px',
    }),
    input: (styles) => ({
      ...styles,
      padding: '0px 8px',
      margin: '0px',
      backgroundColor: primaryColors ? primaryColors.third : colors.primaryBlue_100,
    }),
    dropdownIndicator: (styles) => ({
      ...styles,
      fontSize: '10px',
    }),
  };
  
  return (
    <Wrapper isCustomized={!!customizedTitle} width={width}>
      <Select
        defaultValue={defaultSelected}
        onChange={handleChange}
        options={options}
        components={{
          IndicatorSeparator: () => null,
          DropdownIndicator,
        }}
        styles={isPill ? pillStyles : customStyles}
        isDisabled={isDisabled}
        value={value ? optionMap.get(value) : selected}
        isSearchable={false}
		inputId={inputId}
		aria-label={ariaLabelValue}
      />
      <span>{customizedTitle}</span>
    </Wrapper>
  );
};

export default ReactSelect;
